import { css } from "@emotion/react";
import type { PropsWithChildren } from "react";

import { Flex, Icon, Icons } from "@phoenix/components";
import {
  AnnotationNameAndValue,
} from "@phoenix/components/annotation/AnnotationNameAndValue";

import type { Annotation, AnnotationDisplayPreference } from "./types";

export const baseAnnotationLabelCSS = css`
  border-radius: var(--global-dimension-size-50);
  border: 1px solid var(--global-border-color-default);
  padding: var(--global-dimension-size-50) var(--global-dimension-size-100);
  transition: background-color 0.2s;
  display: flex;
  flex-direction: row;
  gap: var(--global-dimension-size-50);
  &[data-clickable="true"] {
    cursor: pointer;
    &:hover {
      background-color: var(--global-color-gray-300);
    }
  }
  .icon-wrap {
    font-size: 12px;
  }
`;

export function AnnotationLabel({
  annotation,
  onClick,
  annotationDisplayPreference = "score",
  className,
  children,
  clickable: _clickable,
}: PropsWithChildren<{
  annotation: Annotation;
  clickable?: boolean;
  onClick?: () => void;
  annotationDisplayPreference?: AnnotationDisplayPreference;
  className?: string;
}>) {
  const clickable = _clickable ?? typeof onClick === "function";
  const hasScore = typeof annotation.score === "number";
  const isReverseMetric =
    annotation.name.toLowerCase().includes("toxicity") ||
    annotation.name.toLowerCase().includes("hallucination") ||
    annotation.name.toLowerCase().includes("failure_rate") ||
    annotation.name.toLowerCase().includes("failure_rates");

  let shouldBeRed = false;
  let shouldBeGreen = false;

  if (hasScore) {
    if (isReverseMetric) {
      shouldBeRed = annotation.score >= 50;
      shouldBeGreen = annotation.score < 50;
    } else {
      shouldBeRed = annotation.score < 50;
      shouldBeGreen = annotation.score >= 50;
    }
  }

  return (
    <div
      role={clickable ? "button" : undefined}
      data-clickable={clickable}
      className={className}
      css={css(
        baseAnnotationLabelCSS,
        shouldBeRed && css`
          background-color: #ffebee;
          border-color: #ef9a9a;
          color: #c62828;
          .ac-text, svg {
            color: #c62828 !important;
          }
          &[data-clickable="true"]:hover {
            background-color: #ffcdd2;
          }
        `,
        shouldBeGreen && css`
          background-color: #e8f5e9;
          border-color: #a5d6a7;
          color: #2e7d32;
          .ac-text, svg {
            color: #2e7d32 !important;
          }
          &[data-clickable="true"]:hover {
            background-color: #c8e6c9;
          }
        `
      )}
      aria-label={
        clickable
          ? "Click to view the annotation trace"
          : `Annotation: ${annotation.name}`
      }
      onClick={(e) => {
        if (onClick) {
          e.stopPropagation();
          e.preventDefault();
          onClick();
        }
      }}
    >
      <Flex direction="row" gap="size-100" alignItems="center">
        <AnnotationNameAndValue
          annotation={annotation}
          displayPreference={annotationDisplayPreference}
          size="XS"
          maxWidth="none"
        />
        {children}
        {clickable ? <Icon svg={<Icons.ArrowIosForwardOutline />} /> : null}
      </Flex>
    </div>
  );
}
